import * as React from 'react';
import { Slot, Slottable } from '@radix-ui/react-slot';
import { type VariantProps } from 'class-variance-authority';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { PermissionCode } from '@/constants/permissions';
import { VerifyGrantWrapper } from '@/components/VerifyGrantWrapper';
import { buttonVariants } from './variants';
import {
  BUTTON_ICON_PRESETS,
  isButtonIconPreset,
  type ButtonIconPreset,
} from './presets';

export type ButtonProps = React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    /** 是否把样式与属性合并到子元素上（Radix Slot） */
    asChild?: boolean;
    /**
     * 权限码，传入后会判断用户是否有该权限，没有则不渲染
     */
    grant?: PermissionCode;
    /**
     * 图标：内置预设枚举或 lucide 图标组件
     *
     * - 传入 `BUTTON_ICON_PRESETS` 的 key 时使用内置图标，并把对应 variant 作为默认值
     * - 直接传入 `LucideIcon` 组件时同样以 `w-3.5 h-3.5` 渲染在 children 左侧，但不改变 variant
     * - 只有内置预设提供默认 variant，显式 variant 始终优先
     */
    icon?: ButtonIconPreset | LucideIcon;
  };

function Button({
  className,
  variant,
  size = 'default',
  asChild = false,
  grant,
  icon,
  children,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot : 'button';

  // 仅内置预设提供「默认 variant」；显式 variant 优先，undefined 视为未指定并回退到预设
  const preset =
    typeof icon === 'string' && isButtonIconPreset(icon) ? BUTTON_ICON_PRESETS[icon] : undefined;
  const resolvedVariant = variant ?? preset?.variant ?? 'default';

  // 预设取内置图标，自定义 LucideIcon 组件使用同一套尺寸与位置，避免各处重复写 className
  const Icon = preset?.icon ?? (typeof icon === 'string' ? undefined : icon);
  // key 用于 asChild 场景：Slot 会把图标与原子元素重组成数组，缺 key 会触发 React 警告
  const iconNode = Icon ? <Icon key="button-icon" className="w-3.5 h-3.5" /> : null;

  let content: React.ReactNode = children;
  if (iconNode) {
    if (asChild) {
      // Slot 只接受单个 React 元素：用 Slottable 把原子元素交还 Slot，图标作为同级节点注入
      content = [iconNode, <Slottable key="button-slottable">{children}</Slottable>];
    } else {
      // 普通按钮：图标固定渲染在 children 左侧
      content = (
        <>
          {iconNode}
          {children}
        </>
      );
    }
  }

  return (
    <VerifyGrantWrapper grant={grant}>
      <Comp
        type="button"
        data-slot="button"
        data-variant={resolvedVariant}
        data-size={size}
        className={cn(buttonVariants({ variant: resolvedVariant, size, className }), 'cursor-pointer')}
        {...props}
      >
        {content}
      </Comp>
    </VerifyGrantWrapper>
  );
}

export { Button };
